// src/components/team/RecommendToDirectorCard.jsx
//
// Smart-action card shown to solo coaches inviting them to recommend
// Scrimly to their club director. The card opens a composer modal
// (RecommendToDirectorModal) where the coach types the director's
// contact info, reviews a pre-filled email, and clicks "Send via my
// email" — which opens their own mail client via `mailto:`.
//
// Self-hiding eligibility (no parent props needed beyond teamId):
//   - Coach must be the team owner (or coachId fallback)
//   - Team must NOT already be linked to a Scrimly club (team.clubId)
//   - Team must have ≥5 active registrations (signal of real use)
//   - Coach must not have dismissed the card in the last 90 days
//   - Coach must not have already submitted a recommendation for this
//     team (until team.clubId is set, indicating they're a Scrimly club
//     and we should hide the card permanently)
//
// All eligibility checks happen client-side here so TeamSmartActions
// stays simple. The server-side submitClubLead callable re-checks
// ownership + rate limit + dedup as defense in depth.
//
// See features/recommend-to-director/ for the spec package.

import { useEffect, useState } from 'react';
import {
  doc, getDoc, collection, query, where, getDocs, serverTimestamp, updateDoc, setDoc,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import RecommendToDirectorModal from './RecommendToDirectorModal';

const ELIGIBILITY_REG_MIN = 5;
const DISMISSAL_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

export default function RecommendToDirectorCard({ teamId }) {
  // AuthContext exposes { user, userProfile, ... } — NOT `currentUser`.
  // `user` is the Firebase Auth user (has .uid, .email, .displayName);
  // `userProfile` is the Firestore users/{uid} doc (has .firstName,
  // .lastName, .language). Use `user.uid` for the auth check, and
  // prefer profile fields for the display name.
  const { user, userProfile } = useAuth();
  const uid = user?.uid;
  const [eligible, setEligible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [coachInfo, setCoachInfo] = useState({ name: '', teamName: '' });

  // Eligibility check runs once per (teamId, uid). It's NOT a live
  // listener because (a) the inputs change rarely and (b) we want the
  // card visibility to be a snapshot decision, not a flicker on every
  // background write.
  useEffect(() => {
    if (!teamId || !uid) {
      setEligible(false);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        // Load the team to check ownership, clubId, and team name.
        const teamSnap = await getDoc(doc(db, 'teams', teamId));
        if (!teamSnap.exists()) {
          if (!cancelled) setEligible(false);
          return;
        }
        const team = teamSnap.data() || {};
        const isOwner = team.ownerId === uid || team.coachId === uid;
        const inClub = !!(team.clubId && String(team.clubId).trim().length > 0);
        if (!isOwner || inClub) {
          if (!cancelled) setEligible(false);
          return;
        }

        // Check the coach's per-team preferences for dismissal /
        // submission stamps. Uses the existing
        // users/{uid}/teamPreferences/{teamId} sub-doc.
        const prefsSnap = await getDoc(
          doc(db, 'users', uid, 'teamPreferences', teamId)
        );
        const prefs = prefsSnap.exists() ? prefsSnap.data() : {};

        // Once they submit, hide until team.clubId is set (handled by
        // the inClub check above). If they merely dismissed, hide for
        // 90 days then re-eligible.
        if (prefs.submittedRecommendationAt) {
          if (!cancelled) setEligible(false);
          return;
        }
        if (prefs.recommendDismissedAt) {
          const dismissedMs = prefs.recommendDismissedAt.toMillis
            ? prefs.recommendDismissedAt.toMillis()
            : 0;
          if (Date.now() - dismissedMs < DISMISSAL_WINDOW_MS) {
            if (!cancelled) setEligible(false);
            return;
          }
        }

        // Engagement gate: team needs ≥5 active registrations to
        // credibly recommend Scrimly. Counts only paid/confirmed regs.
        // Single Firestore query; no composite index required.
        const regsSnap = await getDocs(
          query(
            collection(db, 'registrations'),
            where('teamId', '==', teamId),
          )
        );
        const activeCount = regsSnap.docs.filter((d) => {
          const s = String(d.data()?.status || '').toLowerCase();
          return s === 'paid' || s === 'confirmed' || s === 'active';
        }).length;
        if (activeCount < ELIGIBILITY_REG_MIN) {
          if (!cancelled) setEligible(false);
          return;
        }

        if (!cancelled) {
          setEligible(true);
          setCoachInfo({
            name:
              userProfile?.displayName
              || [userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ').trim()
              || user?.displayName
              || 'Coach',
            teamName: String(team.name || 'our team'),
          });
        }
      } catch (err) {
        // PII-safe: log to console only, never the eligibility details.
        // eslint-disable-next-line no-console
        console.warn('[RecommendToDirectorCard] eligibility check failed', err?.message);
        if (!cancelled) setEligible(false);
      }
    })();

    return () => { cancelled = true; };
  }, [teamId, uid]);

  // Dismissal: writes the stamp and hides the card. The 90-day re-show
  // window is enforced by the eligibility check above on next mount.
  const dismiss = async () => {
    try {
      await setDoc(
        doc(db, 'users', uid, 'teamPreferences', teamId),
        { recommendDismissedAt: serverTimestamp() },
        { merge: true },
      );
    } catch (err) {
      console.warn('[RecommendToDirectorCard] dismiss write failed', err?.message);
    }
    setEligible(false);
  };

  if (!eligible) return null;

  return (
    <>
      <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-3 min-w-0 dark:border-emerald-900/40 dark:bg-emerald-950/30">
        <div className="flex-shrink-0 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 p-2 text-base leading-none" aria-hidden>
          🤝
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold leading-snug text-emerald-900 dark:text-emerald-100">
            Recommend Scrimly to your club director
          </p>
          <p className="mt-0.5 text-[11px] text-zinc-600 dark:text-zinc-400 leading-snug">
            Most of our best clubs come from a coach saying &ldquo;you should see this.&rdquo; Takes 30 seconds.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-white transition-colors min-h-[36px]"
            >
              Recommend
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="text-[11px] font-medium text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 px-2 py-1.5 min-h-[36px]"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>

      {modalOpen && (
        <RecommendToDirectorModal
          teamId={teamId}
          coachName={coachInfo.name}
          teamName={coachInfo.teamName}
          onClose={() => setModalOpen(false)}
          onSubmitted={() => {
            setModalOpen(false);
            setEligible(false); // hide the card immediately
          }}
        />
      )}
    </>
  );
}
